import { mdiWindsock } from '@mdi/js';
import Icon from '@mdi/react';
import { RadialGauge, RadialGaugeOptions } from 'canvas-gauges';
import React, { useEffect, useRef } from 'react';

interface AmbientData {
  [key: string]: any;
}

interface GaugeWindProps {
  size?: number;
  data: AmbientData;
}

// Fixed conversion: mph to knots is ~0.868976
const mphToKnots = (mph: number): string => (mph * 0.868976).toFixed(1);

export const GaugeWind: React.FC<GaugeWindProps> = ({ size = 300, data }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const gaugeRef = useRef<RadialGauge | null>(null);

  useEffect(() => {
    if (!canvasRef.current) return;

    const options: RadialGaugeOptions = {
      renderTo: canvasRef.current,
      width: size,
      height: size,
      minValue: 0,
      maxValue: 360,
      majorTicks: ["N", "NE", "E", "SE", "S", "SW", "W", "NW", "N"],
      minorTicks: 22,
      ticksAngle: 360,
      startAngle: 180,
      strokeTicks: false,
      highlights: [],
      colorPlate: "#fff",
      colorMajorTicks: "#000",
      colorMinorTicks: "#777",
      colorNumbers: "#000",
      colorNeedle: "red",
      colorNeedleEnd: "red",

      valueBox: false,
      needleType: "line",
      needleStart: 75,
      needleEnd: 99,
      needleWidth: 3,
      borders: true,
      borderOuterWidth: 0,
      colorBorderOuter: "#ddd",
      animationRule: "linear",
      animationDuration: 1000,
      value: data.winddir || 0,
      needleCircleSize: 1,
    };

    gaugeRef.current = new RadialGauge(options).draw();
    return () => gaugeRef.current?.destroy();
  }, [size]);

  // Watch specific properties to trigger needle movement
  useEffect(() => {
    if (gaugeRef.current) {
      gaugeRef.current.value = data.winddir || 0;
    }
  }, [data.winddir]);

  return (
    <div style={{
      position: 'relative',
      width: size,
      height: size,
      margin: '0 auto'
    }}>
      <canvas ref={canvasRef} />

      {/* Central Content Overlay */}
      <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none', fontFamily: 'Arial, sans-serif', }}>
        <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
          <div style={{ fontSize: `${size * 0.12}px`, fontWeight: 'bold', letterSpacing: "-1px", color: '#000' }}>
            {mphToKnots(data.windspeedmph)}
          </div>

          <div style={{ fontSize: `${size * 0.05}px`, color: '#666', textTransform: 'uppercase', marginTop: '-2px' }}>
            knots
          </div>
          <div style={{ fontSize: `${size * 0.07}px`, color: '#444', fontWeight: '600', letterSpacing: '0.5px' }}>
            {data.winddir || 0}° {windDirectionToCompass(data.winddir)}
          </div>
          <div style={{ fontSize: `${size * 0.07}px`, color: '#444', fontWeight: '600', letterSpacing: '0.5px', marginTop: `-${size * 0.02}px` }}>
            <Icon path={mdiWindsock} size={size * 0.0035} />
            <span style={{ marginLeft: '4px' }}>
              {mphToKnots(data.windgustmph)} kt
            </span>
          </div>
        </div>

      </div>
    </div>

  );
};

export default GaugeWind;

// Helper functions moved outside component
function windDirectionToCompass(deg: number): string {
  if (deg == null || isNaN(deg)) return "";
  const directions = ["N", "NNE", "NE", "ENE", "E", "ESE", "SE", "SSE", "S", "SSW", "SW", "WSW", "W", "WNW", "NW", "NNW"];
  const index = Math.round(deg / 22.5) % 16;
  return directions[index];
}